import React, { useState, useEffect, useCallback } from "react";
import { Plus } from "lucide-react";
import GuidelinesPlanCreatePopup from "../Guidelines_Creation/GuidelinesPlanCreatePopup";
import { useToast, ToastContainer } from "../../Ui_Components/ToastMessage";
import { getAllGuidelines } from "../../../services/subPlans";
import { useAuth } from "../../../context/auth/AuthContext";
import PermissionGate from "../../Auth/PermissionGate";
import { Button } from "../../Ui_Components/Button";

interface GuidelinesPlanTabProps {
  userId: number;
  isDeactivated?: boolean;
}

interface Guideline {
  id: number;
  name: string;
  category: string;
  created_by: string;
  created_on: string;
  status: "Active" | "Approval Pending" | "Draft" | "In-Active";
}

const shimmerStyles = `
  @keyframes shimmer {
    0% { background-position: -1000px 0; }
    100% { background-position: 1000px 0; }
  }
  .shimmer {
    animation: shimmer 2s infinite;
    background: linear-gradient(
      to right,
      #f0f0f0 0%,
      #e0e0e0 20%,
      #f0f0f0 40%,
      #f0f0f0 100%
    );
    background-size: 1000px 100%;
  }
`;

const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return "—";
  const datePart = dateStr.toString().substring(0, 10);
  const [year, month, day] = datePart.split("-").map(Number);
  if (!year || !month || !day) return "—";
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const GuidelinesPlanTab: React.FC<GuidelinesPlanTabProps> = ({ userId, isDeactivated = false }) => {
  const { user } = useAuth();
  const nutristId = user?.nutrist_id;
  const nutristName = [user?.nutrist_fname, user?.nutrist_lname]
    .filter(Boolean)
    .join(" ");

  const [activeFilter, setActiveFilter] = useState("all");
  const [allGuidelines, setAllGuidelines] = useState<Guideline[]>([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);
  const [isCreatePopupOpen, setIsCreatePopupOpen] = useState(false);

  const { toasts, removeToast, success, error, warning, info } = useToast();

  const fetchGuidelines = useCallback(async () => {
    if (!userId) {
      setApiError("No user ID provided");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setApiError(null);

      const apiResponse = await getAllGuidelines(userId, "all");

      if (apiResponse.success === "1" && Array.isArray(apiResponse.data)) {
        const mapped: Guideline[] = apiResponse.data.map((item: any) => {
          const createdByDisplay =
            item.created_by && nutristName &&
            item.created_by.toLowerCase().startsWith(nutristName.toLowerCase())
              ? "You"
              : item.created_by ?? "—";

          const rawDate = item.created_at || item.created_date || null;
          const normalizedStatus =
            item.status === "Inactive" ? "In-Active" : item.status;

          return {
            id: item.guideline_detail_id,
            name: item.title,
            category: item.category,
            created_by: createdByDisplay,
            created_on: formatDate(rawDate),
            status: normalizedStatus as Guideline["status"],
          };
        });
        setAllGuidelines(mapped);
      } else {
        throw new Error(apiResponse.message || "Failed to fetch guidelines");
      }
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Failed to load guidelines";
      console.error("❌ Error fetching guidelines:", err);
      setApiError(msg);
    } finally {
      setLoading(false);
    }
  }, [userId, nutristId, nutristName]);

  useEffect(() => {
    fetchGuidelines();
  }, [fetchGuidelines]);

  const filteredGuidelines = allGuidelines.filter((guideline) => {
    if (activeFilter === "all") return true;
    if (activeFilter === "active") return guideline.status === "Active";
    if (activeFilter === "inactive") return guideline.status === "In-Active";
    return true;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Active":           return "bg-[#EBF7F4] text-[#0DB5B5]";
      case "Approval Pending": return "bg-[#FFF3DE] text-[#FF9500]";
      case "Draft":            return "bg-[#E9E9E9] text-[#202020]";
      case "In-Active":        return "bg-[#FFDADA] text-[#FF3B30]";
      default:                 return "bg-gray-100 text-gray-600";
    }
  };

  const handleCreateClick = () => setIsCreatePopupOpen(true);
  const handlePopupClose = () => setIsCreatePopupOpen(false);
  const handleGuidelineSubmit = () => fetchGuidelines();

  const formattedCount = String(filteredGuidelines.length).padStart(2, "0");

  return (
    <>
      <style>{shimmerStyles}</style>
      <ToastContainer toasts={toasts} onClose={removeToast} />

      <div className="h-full flex flex-col bg-white rounded-md border border-[#F3F4F6] overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold font-inter text-[#1F1F1F]">
              Guidelines plans ({formattedCount})
            </h2>
            <PermissionGate resource="guideline" action="create">
              <Button
                variant="primary"
                onClick={() => !isDeactivated && handleCreateClick()}
                disabled={isDeactivated}
                startIcon={<Plus className="w-4 h-4" />}
              >
                Create
              </Button>
            </PermissionGate>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex-shrink-0 px-6 border-b border-[#F3F4F6]">
          <div className="flex gap-6">
            {[
              { id: "all", label: "All" },
              { id: "active", label: "Active" },
              { id: "inactive", label: "Inactive" },
            ].map((filter) => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`pb-2 font-inter text-sm font-medium transition-colors relative ${
                  activeFilter === filter.id
                    ? "text-[#0DB5B5]"
                    : "text-[#8E8E93] hover:text-[#1F1F1F]"
                }`}
              >
                {filter.label}
                {activeFilter === filter.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#0DB5B5]" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Scrollable Table Container */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Fixed Table Header */}
          <div className="flex-shrink-0 overflow-x-auto bg-white border-b border-[#111C2D33] custom-scrollbar-overlay">
            <table className="w-full min-w-[900px] table-fixed">
              <thead>
                <tr className="bg-white">
                  <th className="text-left pt-6 pb-3 px-6 text-[#646464] text-sm 3xl:text-base font-medium font-inter tracking-wider w-[3%]">
                    Sl
                  </th>
                  <th className="text-left pt-6 pb-3 px-6 text-[#646464] text-sm 3xl:text-base font-medium font-inter tracking-wider w-[36%]">
                    Name
                  </th>
                  <th className="text-left pt-6 pb-3 px-6 text-[#646464] text-sm 3xl:text-base font-medium font-inter tracking-wider w-[22%]">
                    Category
                  </th>
                  <th className="text-left pt-6 pb-3 px-6 text-[#646464] text-sm 3xl:text-base font-medium font-inter tracking-wider w-[22%]">
                    Created By
                  </th>
                  <th className="text-left pt-6 pb-3 px-6 text-[#646464] text-sm 3xl:text-base font-medium font-inter tracking-wider w-[22%]">
                    Created On
                  </th>
                </tr>
              </thead>
            </table>
          </div>

          {/* Scrollable Table Body */}
          <div className="flex-1 overflow-x-auto overflow-y-auto custom-scrollbar-overlay">
            {loading ? (
              <div className="h-full shimmer" />
           
            ) : (
              <>
                <table className="w-full min-w-[900px] table-fixed">
                  <tbody className="bg-white divide-y divide-[#F3F4F6]">
                    {filteredGuidelines.map((guideline, index) => (
                      <tr
                        key={guideline.id}
                        className="hover:bg-[#F9FEFE] transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F] w-[3%]">
                          {String(index + 1).padStart(2, "0")}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap w-[36%]">
                          <div className="flex items-center gap-2">
                            <span className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
                              {guideline.name}
                            </span>
                            <span
                              className={`px-1.5 py-0.5 rounded-[24px] text-[8px] 3xl:text-[10px] font-semibold font-inter flex-shrink-0 ${getStatusColor(guideline.status)}`}
                            >
                              {guideline.status}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F] w-[22%]">
                          {guideline.category}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F] w-[22%]">
                          {guideline.created_by}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F] w-[22%]">
                          {guideline.created_on}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {filteredGuidelines.length === 0 && (
                  <div className="px-6 py-12 text-center">
                    <p className="text-sm text-gray-500">No guidelines found</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {!isDeactivated && (
        <GuidelinesPlanCreatePopup
          isOpen={isCreatePopupOpen}
          onClose={handlePopupClose}
          onSubmit={handleGuidelineSubmit}
          userId={userId}
          onShowToast={(type, title, message, duration) => {
            switch (type) {
              case "success": success(title, message, duration); break;
              case "error":   error(title, message, duration);   break;
              case "warning": warning(title, message, duration); break;
              case "info":    info(title, message, duration);    break;
            }
          }}
        />
      )}
    </>
  );
};

export default GuidelinesPlanTab;